import React, { useMemo } from 'react';
import { Layers, CheckSquare, Square } from 'lucide-react';
import { Badge } from './badge';
import { Button } from './button';
import { cn } from '../../lib/utils';
import { DEFAULT_SPORT, normalizeSportValue } from '../../constants/sports';
import { rangoCategoria } from '../../lib/categorias';

export const formatRange = (categoria, sportFallback = DEFAULT_SPORT) =>
  rangoCategoria(categoria, { sportFallback });

export const CategoryMultiSelect = ({
  label = 'Categorias',
  description = 'Selecciona al menos una categoria compatible.',
  options = [],
  selectedIds = [],
  onChange = () => {},
  sport,
  disabled = false,
  emptyMessage = 'No hay categorias disponibles.'
}) => {
  const { optionsToRender, hasSportFilterWithoutResults } = useMemo(() => {
    if (!sport) {
      return { optionsToRender: options, hasSportFilterWithoutResults: false };
    }
    const normalized = normalizeSportValue(sport);
    const matches = options.filter(
      (option) => normalizeSportValue(option.deporte) === normalized
    );
    return {
      optionsToRender: matches,
      hasSportFilterWithoutResults: matches.length === 0 && options.length > 0
    };
  }, [options, sport]);

  const toggleCategory = (id) => {
    if (disabled) return;
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((value) => value !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    const ids = optionsToRender.map((option) => option.id);
    onChange(ids);
  };

  const handleClear = () => {
    if (disabled) return;
    onChange([]);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white/80 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
        <Badge variant="secondary" className="bg-gray-100 text-gray-700">
          <Layers className="mr-1 h-3 w-3" /> {selectedIds.length}
        </Badge>
      </div>

      {hasSportFilterWithoutResults && (
        <p className="mb-2 text-xs text-amber-700">
          No hay categorias registradas para {sport}.
        </p>
      )}

      {optionsToRender.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={handleSelectAll}
            >
              Seleccionar todas
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || selectedIds.length === 0}
              onClick={handleClear}
            >
              Limpiar
            </Button>
          </div>

          <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
            {optionsToRender.map((categoria) => {
              const isSelected = selectedIds.includes(categoria.id);
              return (
                <button
                  type="button"
                  key={categoria.id}
                  disabled={disabled}
                  onClick={() => toggleCategory(categoria.id)}
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-left text-sm transition',
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/40'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-gray-900">
                        {categoria.nombre || 'Categoria sin nombre'}
                      </p>
                      <p className="text-xs text-gray-500">{formatRange(categoria)}</p>
                    </div>
                    {isSelected ? (
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Square className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
